import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  Button,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Checkbox,
  Snackbar,
  Alert,
  Divider,
  Backdrop,
  LinearProgress,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1", "0.0.0.0"];

const resolveApiOrigin = () => {
  const override = process.env.REACT_APP_API_BASE;
  if (override) {
    return override.replace(/\/$/, "");
  }
  if (typeof window === "undefined") {
    return "https://server-9p4hx.kinsta.app";
  }
  const hostname = (window.location.hostname || "").toLowerCase();
  if (
    LOCAL_HOSTNAMES.includes(hostname) ||
    hostname.endsWith(".local") ||
    hostname.startsWith("192.168.")
  ) {
    return "https://server-9p4hx.kinsta.app";
  }
  if (
    hostname === "bo.luxuraelite.ro" ||
    hostname.endsWith(".luxuraelite.ro")
  ) {
    return "https://server-9p4hx.kinsta.app";
  }
  return "https://server-9p4hx.kinsta.app";
};

const API_BASE = resolveApiOrigin();
const FILTERS_API = `${API_BASE}/api/seo-products/filters`;
const PRODUCTS_API = `${API_BASE}/api/seo-products/products`;
const ASSIGN_API = `${API_BASE}/api/seo-products/assign`;
const API_FALLBACK =
  (process.env.REACT_APP_API_FALLBACK || "").replace(/\/$/, "") ||
  "https://server-9p4hx.kinsta.app";
const ATTRIBUTES_API = `${API_BASE}/api/seowoo`;

const initialFilters = {
  category: "",
  subcategory: "",
  brand: "",
  search: "",
};

const initialAvailableFilters = {
  categories: [],
  subcategories: [],
  brands: [],
};

const DEFAULT_SEO_ATTRIBUTE_SLUGS = ["pa_categorie", "pa_categorii"];

const DEFAULT_INCALTAMINTE_SEO_CLUSTERS = [
  {
    title: "Set standard (fără diacritice)",
    terms: [
      "fashion incaltaminte dama",
      "incaltaminte casual dama",
      "incaltaminte piele dama",
      "incaltaminte dama eleganta",
      "incaltaminte din piele naturala dama",
      "magazin incaltaminte dama",
      "incaltaminte online dama",
      "incaltaminte eleganta dama",
      "incaltaminte dama piele",
      "incaltaminte piele naturala",
      "incaltaminte fashion piele",
      "incaltaminte fashion",
      "incaltaminte brand",
      "incaltaminte de lux",
      "incaltaminte de lux dama",
      "branduri de incaltaminte de lux",
      "pantofi de firma",
      "incaltaminte premium",
      "incaltaminte sport de lux",
      "incaltaminte originala",
      "incaltaminte dama de firma",
      "pantofi de brand",
      "pantofi de firma online",
      "incaltaminte de firma dama",
      "pantofi de firma femei",
      "incaltaminte originala dama",
      "incaltaminte dama originala",
      "incaltaminte dama online de firma",
      "incaltaminte de dama de firma",
      "pantofi dama de firma",
      "pantofi dama de firma online",
      "pantofi de firma dama",
      "incaltaminte femei de firma",
      "incaltaminte de firma femei",
      "papuci dama",
      "papuci dama eleganti",
      "papuci dama piele",
      "papuci piele dama",
      "papuci de strada dama",
      "papuci de piele dama",
      "papuci dama de firma",
      "papuci de firma",
      "papuci de firma dama",
      "slapi dama de firma",
      "slapi de firma dama",
      "slapi brand",
      "slapi dama originali",
      "slapi originali dama",
      "papuci dama originali",
      "papuci de firma femei",
      "papuci de lux",
      "papuci originali femei",
      "papuci originali online",
      "papuci premium",
      "papuci firma dama originali",
    ],
  },
  {
    title: "Cluster general / comercial",
    terms: [
      "încălțăminte damă",
      "magazin încălțăminte damă",
      "încălțăminte damă online",
      "încălțăminte damă brand",
      "încălțăminte damă premium",
    ],
  },
  {
    title: "Cluster piele / materiale",
    terms: [
      "încălțăminte damă din piele",
      "încălțăminte damă piele naturală",
      "încălțăminte damă piele",
      "încălțăminte fashion piele",
      "încălțăminte piele naturală",
    ],
  },
  {
    title: "Cluster fashion / design / lux",
    terms: [
      "fashion încălțăminte damă",
      "încălțăminte fashion damă",
      "încălțăminte brand damă",
      "încălțăminte de lux damă",
      "branduri de încălțăminte de lux",
      "încălțăminte designer damă",
    ],
  },
  {
    title: "Cluster elegant / ocazii",
    terms: [
      "încălțăminte damă elegantă",
      "încălțăminte elegantă damă",
      "încălțăminte damă eveniment",
      "încălțăminte damă office elegant",
      "încălțăminte elegantă din piele damă",
    ],
  },
  {
    title: "Cluster casual / uz zilnic",
    terms: [
      "încălțăminte casual damă",
      "încălțăminte damă casual",
      "încălțăminte de zi damă",
      "încălțăminte confortabilă damă",
      "încălțăminte damă casual din piele",
    ],
  },
  {
    title: "Cluster papuci / sandale / încălțăminte deschisă",
    terms: [
      "papuci damă",
      "papuci damă eleganți",
      "papuci piele damă",
      "papuci din piele damă",
      "papuci de stradă damă",
      "papuci damă de lux",
      "papuci damă fashion",
    ],
  },
  {
    title: "Variante long-tail utile",
    terms: [
      "încălțăminte damă din piele naturală premium",
      "încălțăminte elegantă damă din piele",
      "papuci damă din piele naturală",
      "încălțăminte de lux damă online",
      "încălțăminte fashion damă din piele",
      "papuci damă eleganți din piele naturală",
    ],
  },
  {
    title: "Extra Ankle boots",
    terms: [
      "ankle boots",
      "cizme firma",
      "cizme dama firma",
      "cizme de firma dama",
      "cizme de firma",
      "cizme piele dama firma",
      "cizme firma dama",
      "cizme de firma online",
      "cizme dama de firma",
      "cizme dama piele de firma",
      "cizme piele de firma",
      "cizme premium",
      "cizme brand",
      "cizme lux dama",
      "cizme dama originale",
      "cizme dama lux",
      "cizme de lux online",
      "cizme dama piele lux",
      "cizme de lux",
      "cizme piele lux",
      "cizme de lux dama",
      "cizme dama de lux",
      "botine dama firma",
      "botine dama de firma",
      "botine de firma",
      "botine brand",
      "botine firma",
      "botine dama de lux",
      "botine de lux",
      "botine piele de firma",
    ],
  },
  {
    title: "Extra Toc",
    terms: [
      "pantofi cu toc de firma",
      "pantofi cu toc firma",
      "pantofi eleganti de firma",
      "pantofi eleganti de lux",
      "pantofi eleganti lux",
    ],
  },
  {
    title: "Extra Sandale",
    terms: [
      "sandale de firma",
      "sandale dama firma",
      "sandale firma",
      "sandale dama de firma",
      "sandale de firma dama",
      "sandale de lux",
      "sandale lux",
      "sandale de lux dama",
      "sandale firma dama",
      "sandale brand",
      "sandale cu toc de firma",
      "sandale cu platforma de firma",
      "sandale de firma elegante",
      "sandale dama piele de firma",
      "sandale elegante de firma",
      "sandale firma femei",
      "sandale cu platforma firma",
      "sandale de firma cu toc",
      "sandale firma online",
      "sandale piele firma",
      "sandale de firma online",
      "sandale cu toc de lux",
      "sandale dama lux",
      "sandale platforma lux",
      "sandale lux dama",
      "sandale elegante de lux",
      "sandale lux femei",
      "sandale piele lux",
      "sandale lux online",
      "sandale de lux online",
      "sandale dama brand",
      "sandale de brand",
      "sandale cu toc mediu brand",
      "sandale cu toc de brand",
      "sandale cu toc brand",
      "sandale plateforme originale",
      "sandale elegante originale",
      "sandale dama originale",
      "sandale dama premium",
      "sandale premium",
    ],
  },
];

const normalizeText = (value) =>
  (value ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .trim();

const buildTermSearchKey = (term) => {
  const parts = [term?.label, term?.name, term?.slug].filter(Boolean);
  if (Array.isArray(term?.synonyms)) {
    parts.push(...term.synonyms);
  }
  return normalizeText(parts.join(" "));
};

const flattenClusterTerms = (clusters) => {
  const seen = new Set();
  const result = [];
  clusters.forEach((cluster) => {
    (cluster?.terms || []).forEach((term) => {
      const clean = (term || "").trim();
      if (!clean) return;
      const key = clean.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        result.push(clean);
      }
    });
  });
  return result;
};

const DEFAULT_INCALTAMINTE_TERMS = flattenClusterTerms(
  DEFAULT_INCALTAMINTE_SEO_CLUSTERS,
);

const resolveTermId = (term) =>
  term?.id ?? term?.term_id ?? term?.termId ?? term?.slug ?? term?.name;

const extractTermComparableValue = (term) => {
  if (
    typeof term === "string" ||
    typeof term === "number" ||
    typeof term === "boolean"
  ) {
    return term.toString().trim();
  }
  if (term && typeof term === "object") {
    const value =
      term.name ||
      term.label ||
      term.slug ||
      term.value ||
      term.option ||
      term.term ||
      term.term_name ||
      term.termName ||
      term.id ||
      term.term_id ||
      "";
    return value ? value.toString().trim() : "";
  }
  return "";
};

const buildTermPayload = (terms) => {
  const names = [];
  const slugs = [];
  const seenKeys = new Set();

  (Array.isArray(terms) ? terms : []).forEach((term) => {
    let name = "";
    let slug = "";

    if (
      typeof term === "string" ||
      typeof term === "number" ||
      typeof term === "boolean"
    ) {
      name = term.toString().trim();
    } else if (term && typeof term === "object") {
      slug = term.slug ? term.slug.toString().trim() : "";
      name =
        term.name ||
        term.label ||
        term.slug ||
        term.value ||
        term.option ||
        term.term ||
        term.term_name ||
        term.termName ||
        term.id ||
        term.term_id ||
        "";
      name = name ? name.toString().trim() : "";
    }

    const key = slug
      ? `slug:${slug.toLowerCase()}`
      : name
        ? `name:${name.toLowerCase()}`
        : "";
    if (!key || seenKeys.has(key)) {
      return;
    }
    seenKeys.add(key);

    if (name) {
      names.push(name);
    }
    if (slug) {
      slugs.push(slug);
    }
  });

  return { names, slugs };
};

const SEOProductsPage = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [products, setProducts] = useState([]);
  const [availableFilters, setAvailableFilters] = useState(
    initialAvailableFilters,
  );
  const [seoConfig, setSeoConfig] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsError, setProductsError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const [attributes, setAttributes] = useState([]);
  const [selectedAttribute, setSelectedAttribute] = useState("");
  const [attributeTerms, setAttributeTerms] = useState([]);
  const [selectedTerms, setSelectedTerms] = useState([]);

  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncSnackbar, setSyncSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const syncIntervalRef = useRef(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () =>
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));

  const closeSyncSnackbar = () =>
    setSyncSnackbar((prev) => ({
      ...prev,
      open: false,
    }));

  const buildQueryString = (params) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) qs.set(key, value);
    });
    return qs.toString();
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    setProductsError(null);
    setSelectedProducts(new Set());
    try {
      const query = buildQueryString(filters);
      const res = await fetch(
        query ? `${PRODUCTS_API}?${query}` : PRODUCTS_API,
      );
      if (!res.ok) {
        throw new Error("Nu am putut încărca produsele din WooCommerce.");
      }
      const payload = await res.json();
      if (!payload.ok) {
        throw new Error(payload.error?.message || "Eroare necunoscută.");
      }
      setProducts(payload.data?.products || []);
      if (payload.data?.filters) {
        setAvailableFilters(payload.data.filters);
      }
      if (payload.data?.seoConfig) {
        setSeoConfig(payload.data.seoConfig);
      }
    } catch (err) {
      setProductsError(err.message || "A apărut o eroare.");
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchAvailableFilters = async () => {
    try {
      const res = await fetch(FILTERS_API);
      if (!res.ok) {
        throw new Error("Nu am reușit să încărcăm filtrele.");
      }
      const payload = await res.json();
      if (!payload.ok) {
        throw new Error(payload.error?.message || "Eroare necunoscută.");
      }
      setAvailableFilters(payload.data?.filters || initialAvailableFilters);
    } catch (err) {
      showSnackbar(
        err.message || "Nu am putut încărca filtrele de produse.",
        "error",
      );
    }
  };

  const fetchAttributes = async () => {
    try {
      const res = await fetch(`${ATTRIBUTES_API}/attributes`);
      if (!res.ok) {
        throw new Error("Nu am reușit să preluăm lista atributelor.");
      }
      const payload = await res.json();
      if (!payload.ok) {
        throw new Error(payload.error?.message || "Eroare necunoscută.");
      }
      const normalized =
        payload.data?.attributes ??
        (Array.isArray(payload.data) ? payload.data : []);
      setAttributes(Array.isArray(normalized) ? normalized : []);
    } catch (err) {
      showSnackbar(err.message || "Nu am putut încărca atributele.", "error");
    }
  };

  const fetchAttributeTerms = async (attributeId) => {
    if (!attributeId) {
      setAttributeTerms([]);
      return;
    }
    try {
      const res = await fetch(
        `${ATTRIBUTES_API}/attributes/${attributeId}/terms`,
      );
      if (!res.ok) {
        throw new Error("Nu am putut încărca termenii atributului.");
      }
      const payload = await res.json();
      if (!payload.ok) {
        throw new Error(payload.error?.message || "Eroare necunoscută.");
      }
      const terms =
        payload.data?.terms ??
        (Array.isArray(payload.data) ? payload.data : []);
      setAttributeTerms(Array.isArray(terms) ? terms : []);
    } catch (err) {
      showSnackbar(err.message || "Nu am putut încărca termenii.", "error");
    }
  };

  useEffect(() => {
    fetchAttributes();
  }, []);

  useEffect(() => {
    fetchAvailableFilters();
  }, []);

  useEffect(() => {
    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => {
    setFilters(initialFilters);
    setHasSearched(false);
    setProducts([]);
    setProductsError(null);
    setSelectedProducts(new Set());
    fetchAvailableFilters();
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const handleApplyFilters = () => {
    setHasSearched(true);
    fetchProducts();
  };

  const handleToggleSelectAll = () => {
    setSelectedProducts((prev) => {
      if (prev.size === displayProducts.length) {
        return new Set();
      }
      return new Set(displayProducts.map((product) => product.id));
    });
  };

  const handleAttributeChange = (event) => {
    const value = event.target.value;
    setSelectedAttribute(value);
    setSelectedTerms([]);
    fetchAttributeTerms(value);
  };

  const assignTermsToProducts = async ({
    targetProducts,
    attributeId,
    termPayload,
    successMessage,
  }) => {
    const categoryAttributeId = ensureCategoryAttributeId(attributeId);
    if (!categoryAttributeId) {
      showSnackbar(
        "Atributul selectat nu este valid pentru actualizare.",
        "error",
      );
      return;
    }

    const payload = termPayload || { names: [], slugs: [] };
    const normalizedTerms = Array.isArray(payload.names) ? payload.names : [];
    const normalizedSlugs = Array.isArray(payload.slugs) ? payload.slugs : [];

    if (!normalizedTerms.length && !normalizedSlugs.length) {
      showSnackbar("Nu există termeni validați pentru actualizare.", "warning");
      return;
    }

    if (!targetProducts?.length) {
      showSnackbar(
        "Nu există produse selectate pentru actualizare.",
        "warning",
      );
      return;
    }

    const productSkus = targetProducts
      .map((product) => product.sku)
      .filter(Boolean);
    if (!productSkus.length) {
      showSnackbar(
        "Produsele selectate nu au SKU-uri valide pentru actualizare.",
        "error",
      );
      return;
    }

    setActionMessage(
      `Aplicăm termeni pentru ${targetProducts.length} produs${
        targetProducts.length === 1 ? "" : "e"
      }...`,
    );
    setAssigning(true);
    try {
      const requestBody = JSON.stringify({
        productSkus,
        attributeId: categoryAttributeId,
        termNames: normalizedTerms,
        termSlugs: normalizedSlugs,
      });
      const doAssign = async (url) => {
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: requestBody,
        });
        if (!res.ok) {
          throw new Error("Nu am putut actualiza produsele.");
        }
        return res.json();
      };

      let payload;
      try {
        payload = await doAssign(ASSIGN_API);
      } catch (primaryErr) {
        const isNetworkError =
          primaryErr instanceof TypeError ||
          String(primaryErr?.message || "")
            .toLowerCase()
            .includes("failed to fetch");
        const canFallback =
          isNetworkError &&
          API_BASE.includes("localhost") &&
          API_FALLBACK &&
          !API_FALLBACK.includes("localhost");
        if (!canFallback) {
          throw primaryErr;
        }
        showSnackbar(
          "Serverul local nu răspunde. Folosesc fallback-ul remote.",
          "warning",
        );
        payload = await doAssign(`${API_FALLBACK}/api/seo-products/assign`);
      }

      if (!payload.ok) {
        throw new Error(payload.error?.message || "Eroare necunoscută.");
      }
      const updatedCount = payload.data?.updated || 0;
      const failed = payload.data?.failed || [];
      if (failed.length) {
        const severity =
          failed.length === (payload.data?.attempted || 0)
            ? "error"
            : "warning";
        showSnackbar(
          `Actualizare parțială: ${updatedCount} produse ok, ${failed.length} eșuate.`,
          severity,
        );
      } else {
        showSnackbar(
          successMessage ||
            `Termenii au fost alocați pentru ${updatedCount} produse.`,
          "success",
        );
      }
      setSelectedProducts(new Set());
      fetchProducts();
    } catch (err) {
      showSnackbar(
        err.message || "Eroare la actualizarea produselor.",
        "error",
      );
    } finally {
      setAssigning(false);
      setActionMessage("");
    }
  };

  const handleApplyIncaltaminteSeo = (targetList) => {
    const targets =
      targetList && targetList.length ? targetList : productsNeedingSeo;
    if (!targets.length) {
      showSnackbar(
        "Nu există produse de încălțăminte fără termeni obligatorii.",
        "info",
      );
      return;
    }
    const attributeId = ensureCategoryAttributeId(
      incaltaminteAttributeId ||
        targets.find((product) => product.seoAttributeId)?.seoAttributeId,
    );
    assignTermsToProducts({
      targetProducts: targets,
      attributeId,
      termPayload: buildTermPayload(requiredIncaltaminteTerms),
      successMessage: "Termenii obligatorii au fost adăugați cu succes.",
    });
  };

  const handlePrefillAnkleBootsTerms = () => {
    if (!isAnkleBootsCategory || !ankleBootsTerms.length) {
      showSnackbar(
        "Selectează categoria Ankle Boots pentru acest set.",
        "info",
      );
      return;
    }
    const normalizedExisting = new Set(
      (selectedTerms || []).map((term) =>
        normalizeText(extractTermComparableValue(term)),
      ),
    );
    const toAdd = ankleBootsTerms.filter(
      (term) => !normalizedExisting.has(normalizeText(term)),
    );
    if (!toAdd.length) {
      showSnackbar("Termenii Ankle Boots sunt deja în selecție.", "success");
      return;
    }
    const extraSelections = toAdd.map((term) => {
      const match = termOptions.find(
        (option) =>
          normalizeText(option.label || option.name) === normalizeText(term),
      );
      return match || { label: term, name: term };
    });
    setSelectedTerms((prev) => [...prev, ...extraSelections]);
    if (!selectedAttribute && categoryAttributes.length) {
      setSelectedAttribute(categoryAttributes[0].id);
    }
    showSnackbar("Am adăugat termenii Ankle Boots în selecție.", "success");
  };

  const handleApplyAnkleBootsTermsToAll = () => {
    if (!isAnkleBootsCategory || !ankleBootsTerms.length) {
      showSnackbar(
        "Selectează categoria Ankle Boots pentru acest set.",
        "info",
      );
      return;
    }
    if (!displayProducts.length) {
      showSnackbar("Nu există produse filtrate pentru aplicare.", "info");
      return;
    }
    const attributeId = ensureCategoryAttributeId(
      incaltaminteAttributeId ||
        displayProducts.find((product) => product.seoAttributeId)
          ?.seoAttributeId,
    );
    assignTermsToProducts({
      targetProducts: displayProducts,
      attributeId,
      termPayload: buildTermPayload(ankleBootsTerms),
      successMessage: "Termenii Ankle Boots au fost adăugați cu succes.",
    });
  };

  const handlePrefillTocTerms = () => {
    if (!isTocCategory || !tocTerms.length) {
      showSnackbar("Selectează categoria Toc pentru acest set.", "info");
      return;
    }
    const normalizedExisting = new Set(
      (selectedTerms || []).map((term) =>
        normalizeText(extractTermComparableValue(term)),
      ),
    );
    const toAdd = tocTerms.filter(
      (term) => !normalizedExisting.has(normalizeText(term)),
    );
    if (!toAdd.length) {
      showSnackbar("Termenii cu toc sunt deja în selecție.", "success");
      return;
    }
    const extraSelections = toAdd.map((term) => {
      const match = termOptions.find(
        (option) =>
          normalizeText(option.label || option.name) === normalizeText(term),
      );
      return match || { label: term, name: term };
    });
    setSelectedTerms((prev) => [...prev, ...extraSelections]);
    if (!selectedAttribute && categoryAttributes.length) {
      setSelectedAttribute(categoryAttributes[0].id);
    }
    showSnackbar("Am adăugat termenii cu toc în selecție.", "success");
  };

  const handleApplyTocTermsToAll = () => {
    if (!isTocCategory || !tocTerms.length) {
      showSnackbar("Selectează categoria Toc pentru acest set.", "info");
      return;
    }
    if (!displayProducts.length) {
      showSnackbar("Nu există produse filtrate pentru aplicare.", "info");
      return;
    }
    const attributeId = ensureCategoryAttributeId(
      incaltaminteAttributeId ||
        displayProducts.find((product) => product.seoAttributeId)
          ?.seoAttributeId,
    );
    assignTermsToProducts({
      targetProducts: displayProducts,
      attributeId,
      termPayload: buildTermPayload(tocTerms),
      successMessage: "Termenii cu toc au fost adăugați cu succes.",
    });
  };

  const handlePrefillSandaleTerms = () => {
    if (!isSandaleCategory || !sandaleTerms.length) {
      showSnackbar("Selectează categoria Sandale pentru acest set.", "info");
      return;
    }
    const normalizedExisting = new Set(
      (selectedTerms || []).map((term) =>
        normalizeText(extractTermComparableValue(term)),
      ),
    );
    const toAdd = sandaleTerms.filter(
      (term) => !normalizedExisting.has(normalizeText(term)),
    );
    if (!toAdd.length) {
      showSnackbar("Termenii Sandale sunt deja în selecție.", "success");
      return;
    }
    const extraSelections = toAdd.map((term) => {
      const match = termOptions.find(
        (option) =>
          normalizeText(option.label || option.name) === normalizeText(term),
      );
      return match || { label: term, name: term };
    });
    setSelectedTerms((prev) => [...prev, ...extraSelections]);
    if (!selectedAttribute && categoryAttributes.length) {
      setSelectedAttribute(categoryAttributes[0].id);
    }
    showSnackbar("Am adăugat termenii Sandale în selecție.", "success");
  };

  const handleApplySandaleTermsToAll = () => {
    if (!isSandaleCategory || !sandaleTerms.length) {
      showSnackbar("Selectează categoria Sandale pentru acest set.", "info");
      return;
    }
    if (!displayProducts.length) {
      showSnackbar("Nu există produse filtrate pentru aplicare.", "info");
      return;
    }
    const attributeId = ensureCategoryAttributeId(
      incaltaminteAttributeId ||
        displayProducts.find((product) => product.seoAttributeId)
          ?.seoAttributeId,
    );
    assignTermsToProducts({
      targetProducts: displayProducts,
      attributeId,
      termPayload: buildTermPayload(sandaleTerms),
      successMessage: "Termenii Sandale au fost adăugați cu succes.",
    });
  };

  const selectedTermPayload = useMemo(
    () => buildTermPayload(selectedTerms),
    [selectedTerms],
  );

  const selectedTermNames = selectedTermPayload.names;

  const normalizedSelectedTermKeys = useMemo(
    () => selectedTermNames.map((name) => normalizeText(name)).filter(Boolean),
    [selectedTermNames],
  );

  const selectedTermLabelMap = useMemo(() => {
    const map = new Map();
    (selectedTerms || []).forEach((term) => {
      const value = extractTermComparableValue(term);
      const normalized = normalizeText(value);
      if (!normalized || map.has(normalized)) return;
      const label =
        term?.label ||
        (term?.slug && value
          ? `${value} (${term.slug})`
          : value || term?.slug || "");
      if (label) {
        map.set(normalized, label);
      }
    });
    selectedTermNames.forEach((name) => {
      const normalized = normalizeText(name);
      if (normalized && !map.has(normalized)) {
        map.set(normalized, name);
      }
    });
    return map;
  }, [selectedTerms, selectedTermNames]);

  const handleSelectMissingSeo = () => {
    setSelectedProducts(
      new Set(productsNeedingSeo.map((product) => product.id)),
    );
  };

  const handleAssignTerms = (applyToAll = false) => {
    if (!selectedAttribute || selectedTerms.length === 0) {
      showSnackbar(
        "Selectează atributul și termenii înainte de a aplica modificările.",
        "warning",
      );
      return;
    }

    const targetProducts = applyToAll
      ? displayProducts
      : displayProducts.filter((product) => selectedProducts.has(product.id));

    if (!targetProducts.length) {
      showSnackbar(
        "Selectează cel puțin un produs sau folosește aplica pe rezultatele filtrate.",
        "warning",
      );
      return;
    }

    assignTermsToProducts({
      targetProducts,
      attributeId: selectedAttribute,
      termPayload: selectedTermPayload,
    });
  };

  const handleSelectProductsMissingSelectedTerms = () => {
    if (!hasSelectedTermFilter) {
      showSnackbar(
        "Selectează mai întâi termenii pe care să îi verificăm.",
        "info",
      );
      return;
    }
    if (!productsMissingSelectedTerms.length) {
      showSnackbar(
        "Toate produsele afișate conțin termenii selectați.",
        "success",
      );
      return;
    }
    setSelectedProducts(
      new Set(productsMissingSelectedTerms.map((product) => product.id)),
    );
  };

  const handleApplyTermsToMissingSelectedProducts = () => {
    if (!hasSelectedTermFilter) {
      showSnackbar("Selectează termenii pe care vrei să îi aplici.", "info");
      return;
    }
    if (!selectedAttribute) {
      showSnackbar(
        "Selectează atributul pentru care aplici termenii.",
        "warning",
      );
      return;
    }
    if (!productsMissingSelectedTerms.length) {
      showSnackbar("Nu există produse fără termenii selectați.", "info");
      return;
    }
    assignTermsToProducts({
      targetProducts: productsMissingSelectedTerms,
      attributeId: selectedAttribute,
      termPayload: selectedTermPayload,
      successMessage:
        "Termenii selectați au fost aplicați pe produsele fără aceștia.",
    });
  };

  const handleSyncProducts = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncProgress(0);
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
    }
    syncIntervalRef.current = setInterval(() => {
      setSyncProgress((prev) => {
        if (prev >= 90) return prev;
        return Math.min(prev + Math.random() * 8 + 4, 90);
      });
    }, 500);

    try {
      const res = await fetch("https://server-9p4hx.kinsta.app/sync-products");
      if (!res.ok) {
        throw new Error("Serverul local a răspuns cu eroare.");
      }
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
      setSyncProgress(100);
      setSyncSnackbar({
        open: true,
        message: "Sincronizare finalizată cu succes!",
        severity: "success",
      });
      fetchAvailableFilters();
      if (hasSearched) {
        fetchProducts();
      }
    } catch (error) {
      console.error("Eroare la sincronizare:", error);
      setSyncSnackbar({
        open: true,
        message: "Nu am reușit să sincronizăm produsele.",
        severity: "error",
      });
    } finally {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
      setTimeout(() => {
        setSyncing(false);
        setSyncProgress(0);
      }, 600);
    }
  };

  const selectedCount = selectedProducts.size;

  const seoAttributeSlugs =
    seoConfig?.seoAttributeSlugs || DEFAULT_SEO_ATTRIBUTE_SLUGS;

  const resolveAttributeKey = useCallback((attr) => {
    if (!attr) return "";
    const slug = (attr.slug || "").toLowerCase();
    const taxonomy = (attr.taxonomy || "").toLowerCase();
    return slug.startsWith("pa_") ? slug : taxonomy || slug;
  }, []);

  const categoryAttributes = useMemo(
    () =>
      attributes.filter((attr) =>
        seoAttributeSlugs.includes(resolveAttributeKey(attr)),
      ),
    [attributes, seoAttributeSlugs, resolveAttributeKey],
  );

  const categoryAttributeIdSet = useMemo(() => {
    return new Set(categoryAttributes.map((attr) => attr.id));
  }, [categoryAttributes]);

  const ensureCategoryAttributeId = useCallback(
    (candidateId) => {
      if (candidateId && categoryAttributeIdSet.has(candidateId)) {
        return candidateId;
      }
      return categoryAttributes[0]?.id || null;
    },
    [categoryAttributeIdSet, categoryAttributes],
  );

  useEffect(() => {
    if (!selectedAttribute && categoryAttributes.length) {
      const defaultId = categoryAttributes[0].id;
      setSelectedAttribute(defaultId);
      fetchAttributeTerms(defaultId);
    }
  }, [categoryAttributes, selectedAttribute]);

  const attributeOptions = useMemo(
    () =>
      categoryAttributes.map((attr) => ({
        value: attr.id,
        label: attr.name,
      })),
    [categoryAttributes],
  );

  const termOptions = useMemo(
    () =>
      attributeTerms.map((term) => {
        const displayName = term.name || term.label || term.slug || "";
        const slug = term.slug ? ` (${term.slug})` : "";
        const label = `${displayName}${slug}`;
        return {
          ...term,
          label,
          id: resolveTermId(term),
          searchKey: buildTermSearchKey({ ...term, label }),
        };
      }),
    [attributeTerms],
  );

  const hasSelectedTermFilter = normalizedSelectedTermKeys.length > 0;

  const filterTermOptions = useCallback((options, { inputValue }) => {
    const normalizedQuery = normalizeText(inputValue);
    if (!normalizedQuery) {
      return options;
    }
    const tokens = normalizedQuery.split(" ").filter(Boolean);
    if (!tokens.length) {
      return options;
    }
    const filtered = options.filter((option) => {
      const haystack =
        option.searchKey || buildTermSearchKey(option) || normalizeText("");
      return tokens.every((token) => haystack.includes(token));
    });
    const exactMatches = [];
    const partialMatches = [];
    filtered.forEach((option) => {
      const haystack =
        option.searchKey || buildTermSearchKey(option) || normalizeText("");
      if (haystack === normalizedQuery) {
        exactMatches.push(option);
      } else {
        partialMatches.push(option);
      }
    });
    return [...exactMatches, ...partialMatches];
  }, []);

  const baseIncaltaminteClusters =
    seoConfig?.incaltaminteClusters || DEFAULT_INCALTAMINTE_SEO_CLUSTERS;
  const selectedCategoryKey = normalizeText(filters.category);
  const selectedSubcategoryKey = normalizeText(filters.subcategory);
  const selectedFiltersKey = normalizeText(
    `${filters.category || ""} ${filters.subcategory || ""}`,
  );
  const isAnkleBootsCategory =
    selectedFiltersKey.includes("ankle boots") ||
    selectedFiltersKey.includes("boots");
  const isTocCategory = selectedFiltersKey.includes("toc");
  const isSandaleCategory =
    selectedFiltersKey.includes("sandale") ||
    selectedFiltersKey.includes("slapi");
  const isIncaltaminteCategory =
    selectedCategoryKey.includes("incaltaminte") ||
    selectedSubcategoryKey.includes("incaltaminte");
  const shouldShowSeoDetection =
    isAnkleBootsCategory ||
    isTocCategory ||
    isSandaleCategory ||
    isIncaltaminteCategory;
  const ankleBootsCluster = baseIncaltaminteClusters.find(
    (cluster) => cluster?.title === "Extra Ankle boots",
  );
  const tocCluster = baseIncaltaminteClusters.find(
    (cluster) => cluster?.title === "Extra Toc",
  );
  const sandaleCluster = baseIncaltaminteClusters.find(
    (cluster) => cluster?.title === "Extra Sandale",
  );
  const fallbackAnkleBootsTerms =
    DEFAULT_INCALTAMINTE_SEO_CLUSTERS.find(
      (cluster) => cluster?.title === "Extra Ankle boots",
    )?.terms || [];
  const fallbackTocTerms =
    DEFAULT_INCALTAMINTE_SEO_CLUSTERS.find(
      (cluster) => cluster?.title === "Extra Toc",
    )?.terms || [];
  const fallbackSandaleTerms =
    DEFAULT_INCALTAMINTE_SEO_CLUSTERS.find(
      (cluster) => cluster?.title === "Extra Sandale",
    )?.terms || [];
  const ankleBootsTerms = ankleBootsCluster?.terms?.length
    ? ankleBootsCluster.terms
    : fallbackAnkleBootsTerms;
  const tocTerms = tocCluster?.terms?.length
    ? tocCluster.terms
    : fallbackTocTerms;
  const sandaleTerms = sandaleCluster?.terms?.length
    ? sandaleCluster.terms
    : fallbackSandaleTerms;

  const incaltaminteClusters = useMemo(() => {
    const base = baseIncaltaminteClusters.filter(
      (cluster) =>
        cluster?.title !== "Extra Ankle boots" &&
        cluster?.title !== "Extra Toc" &&
        cluster?.title !== "Extra Sandale",
    );
    if (isAnkleBootsCategory && ankleBootsTerms.length && ankleBootsCluster) {
      base.push(ankleBootsCluster);
    }
    if (isTocCategory && tocTerms.length && tocCluster) {
      base.push(tocCluster);
    }
    if (isSandaleCategory && sandaleTerms.length && sandaleCluster) {
      base.push(sandaleCluster);
    }
    return base;
  }, [
    baseIncaltaminteClusters,
    isAnkleBootsCategory,
    ankleBootsTerms,
    ankleBootsCluster,
    isTocCategory,
    tocTerms,
    tocCluster,
    isSandaleCategory,
    sandaleTerms,
    sandaleCluster,
  ]);

  const requiredIncaltaminteTerms = useMemo(() => {
    const baseRequired =
      seoConfig?.incaltaminteRequiredTerms || DEFAULT_INCALTAMINTE_TERMS;
    if (seoConfig?.incaltaminteRequiredTerms) {
      const seen = new Set(baseRequired.map((term) => term.toLowerCase()));
      const merged = [...baseRequired];
      if (isAnkleBootsCategory && ankleBootsTerms.length) {
        ankleBootsTerms.forEach((term) => {
          const key = term.toLowerCase();
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(term);
          }
        });
      }
      if (isTocCategory && tocTerms.length) {
        tocTerms.forEach((term) => {
          const key = term.toLowerCase();
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(term);
          }
        });
      }
      if (isSandaleCategory && sandaleTerms.length) {
        sandaleTerms.forEach((term) => {
          const key = term.toLowerCase();
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(term);
          }
        });
      }
      return merged;
      return baseRequired;
    }
    return flattenClusterTerms(incaltaminteClusters);
  }, [
    seoConfig?.incaltaminteRequiredTerms,
    isAnkleBootsCategory,
    ankleBootsCluster,
    ankleBootsTerms,
    isTocCategory,
    tocCluster,
    tocTerms,
    isSandaleCategory,
    sandaleCluster,
    sandaleTerms,
    incaltaminteClusters,
  ]);

  const incaltaminteAttribute = useMemo(
    () =>
      attributes.find((attr) =>
        seoAttributeSlugs.includes(resolveAttributeKey(attr)),
      ),
    [attributes, seoAttributeSlugs, resolveAttributeKey],
  );

  const incaltaminteAttributeId = incaltaminteAttribute?.id;

  const extractAttributeTerms = useCallback(
    (product) => {
      const candidateId =
        product?.seoAttributeId ??
        product?.attributeId ??
        incaltaminteAttributeId;
      let attrs = product?.attributes;
      if (typeof attrs === "string") {
        try {
          attrs = JSON.parse(attrs);
        } catch (error) {
          attrs = null;
        }
      }
      if (!Array.isArray(attrs) || !attrs.length) {
        return [];
      }
      let match = null;
      if (candidateId != null) {
        match = attrs.find((attr) => String(attr?.id) === String(candidateId));
      }
      if (!match) {
        match = attrs.find((attr) =>
          seoAttributeSlugs.includes(resolveAttributeKey(attr)),
        );
      }
      if (!match || !Array.isArray(match.options)) {
        return [];
      }
      return match.options.filter(Boolean);
    },
    [incaltaminteAttributeId, seoAttributeSlugs, resolveAttributeKey],
  );

  const collectProductTermsNormalized = useCallback(
    (product) => {
      const normalizedTerms = new Set();
      if (Array.isArray(product?.seoTermsNormalized)) {
        product.seoTermsNormalized.forEach((term) => {
          const normalized = normalizeText(term);
          if (normalized) {
            normalizedTerms.add(normalized);
          }
        });
      }
      if (Array.isArray(product?.seoTerms)) {
        product.seoTerms.forEach((term) => {
          const normalized = normalizeText(term);
          if (normalized) {
            normalizedTerms.add(normalized);
          }
        });
      }
      extractAttributeTerms(product).forEach((term) => {
        const normalized = normalizeText(term);
        if (normalized) {
          normalizedTerms.add(normalized);
        }
      });
      return normalizedTerms;
    },
    [extractAttributeTerms],
  );

  const requiredIncaltaminteTermKeys = useMemo(
    () =>
      requiredIncaltaminteTerms
        .map((term) => normalizeText(term))
        .filter(Boolean),
    [requiredIncaltaminteTerms],
  );

  const productsWithSeoNeed = useMemo(() => {
    if (!requiredIncaltaminteTermKeys.length) {
      return products;
    }
    return products.map((product) => {
      const productAttributeId = product.seoAttributeId ?? product.attributeId;
      const shouldEvaluate = shouldShowSeoDetection
        ? true
        : incaltaminteAttributeId
          ? productAttributeId &&
            String(productAttributeId) === String(incaltaminteAttributeId)
          : productAttributeId != null;

      if (!shouldEvaluate) {
        return product;
      }

      const normalizedTerms = collectProductTermsNormalized(product);

      const missing = requiredIncaltaminteTermKeys.filter(
        (key) => !normalizedTerms.has(key),
      );
      return {
        ...product,
        needsSeoTerms: missing.length > 0,
      };
    });
  }, [
    products,
    requiredIncaltaminteTermKeys,
    incaltaminteAttributeId,
    shouldShowSeoDetection,
    collectProductTermsNormalized,
  ]);

  const displayProducts = productsWithSeoNeed;

  const productsNeedingSeo = useMemo(
    () => displayProducts.filter((product) => product.needsSeoTerms),
    [displayProducts],
  );

  const selectionEvaluation = useMemo(() => {
    if (!hasSelectedTermFilter) {
      return {
        selectionStatusByProduct: new Map(),
        productsMissingSelectedTerms: [],
      };
    }

    const statusMap = new Map();
    const missingProducts = [];

    displayProducts.forEach((product) => {
      const productSet = collectProductTermsNormalized(product);

      const missingKeys = normalizedSelectedTermKeys.filter(
        (key) => !productSet.has(key),
      );
      const missingLabels = missingKeys.map(
        (key) => selectedTermLabelMap.get(key) || key,
      );

      const status = {
        hasAll: missingKeys.length === 0,
        missingKeys,
        missingLabels,
      };

      statusMap.set(product.id, status);
      if (missingKeys.length) {
        missingProducts.push(product);
      }
    });

    return {
      selectionStatusByProduct: statusMap,
      productsMissingSelectedTerms: missingProducts,
    };
  }, [
    hasSelectedTermFilter,
    displayProducts,
    normalizedSelectedTermKeys,
    selectedTermLabelMap,
    collectProductTermsNormalized,
  ]);

  const { selectionStatusByProduct, productsMissingSelectedTerms } =
    selectionEvaluation;

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            SEO Products
          </Typography>
          <Typography color="text.secondary">
            Filtrează produsele WooCommerce și alocă rapid termeni de atribut
            pentru grupuri întregi.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={`${hasSearched ? displayProducts.length : 0} rezultate`}
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${selectedCount} selectate`}
            color={selectedCount ? "success" : "default"}
            variant="outlined"
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleSyncProducts}
            disabled={syncing}
          >
            Sync produse
          </Button>
        </Stack>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 4,
          mb: 3,
          background: "rgba(255,255,255,0.9)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "flex-end" }}
        >
          <Autocomplete
            fullWidth
            options={availableFilters.categories || []}
            value={filters.category || null}
            onChange={(_, value) => handleFilterChange("category", value || "")}
            renderInput={(params) => (
              <TextField {...params} label="Categorie" placeholder="Caută..." />
            )}
          />
          <Autocomplete
            fullWidth
            options={availableFilters.subcategories || []}
            value={filters.subcategory || null}
            onChange={(_, value) =>
              handleFilterChange("subcategory", value || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Subcategorie"
                placeholder="Caută..."
              />
            )}
          />
          <Autocomplete
            fullWidth
            options={availableFilters.brands || []}
            value={filters.brand || null}
            onChange={(_, value) => handleFilterChange("brand", value || "")}
            renderInput={(params) => (
              <TextField {...params} label="Brand" placeholder="Caută..." />
            )}
          />
          <TextField
            label="Caută după denumire"
            fullWidth
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleApplyFilters();
              }
            }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<FilterAltIcon />}
              onClick={handleApplyFilters}
            >
              Aplică
            </Button>
            <Button variant="text" onClick={handleResetFilters}>
              Reset
            </Button>
            <IconButton onClick={handleApplyFilters}>
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Stack>
        {hasSearched && shouldShowSeoDetection && (
          <Alert
            severity={productsNeedingSeo.length ? "warning" : "success"}
            sx={{ mt: 2, borderRadius: 3 }}
            action={
              productsNeedingSeo.length ? (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleSelectMissingSeo}
                    disabled={assigning}
                  >
                    Selectează lipsă
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<AutoFixHighIcon />}
                    onClick={() => handleApplyIncaltaminteSeo()}
                    disabled={assigning}
                  >
                    Aplică pe toate
                  </Button>
                </Stack>
              ) : null
            }
          >
            <Typography variant="subtitle2" fontWeight={600}>
              {filters.category || "Categorie neselectată"} /{" "}
              {filters.subcategory || "Subcategorie neselectată"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {productsNeedingSeo.length
                ? `${productsNeedingSeo.length} produse filtrate nu au termenii obligatorii.`
                : "Toate produsele filtrate au termenii obligatorii."}
            </Typography>
            {isAnkleBootsCategory && ankleBootsTerms.length ? (
              <Box mt={1}>
                <Typography variant="caption" fontWeight={600}>
                  Termeni Ankle Boots:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {" "}
                  – {ankleBootsTerms.join(", ")}
                </Typography>
                <Box mt={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handlePrefillAnkleBootsTerms}
                  >
                    Adaugă termenii în selecție
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={handleApplyAnkleBootsTermsToAll}
                    sx={{ ml: 1 }}
                  >
                    Aplică la toate produsele filtrate
                  </Button>
                </Box>
              </Box>
            ) : null}
            {isTocCategory && tocTerms.length ? (
              <Box mt={1}>
                <Typography variant="caption" fontWeight={600}>
                  Termeni Toc:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {" "}
                  – {tocTerms.join(", ")}
                </Typography>
                <Box mt={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handlePrefillTocTerms}
                  >
                    Adaugă termenii în selecție
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={handleApplyTocTermsToAll}
                    sx={{ ml: 1 }}
                  >
                    Aplică la toate produsele filtrate
                  </Button>
                </Box>
              </Box>
            ) : null}
            {isSandaleCategory && sandaleTerms.length ? (
              <Box mt={1}>
                <Typography variant="caption" fontWeight={600}>
                  Termeni Sandale:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {" "}
                  – {sandaleTerms.join(", ")}
                </Typography>
                <Box mt={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handlePrefillSandaleTerms}
                  >
                    Adaugă termenii în selecție
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={handleApplySandaleTermsToAll}
                    sx={{ ml: 1 }}
                  >
                    Aplică la toate produsele filtrate
                  </Button>
                </Box>
              </Box>
            ) : null}
          </Alert>
        )}
        {hasSelectedTermFilter && (
          <Alert
            severity={
              productsMissingSelectedTerms.length ? "warning" : "success"
            }
            sx={{ mt: 2, borderRadius: 3 }}
            action={
              productsMissingSelectedTerms.length ? (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems="flex-start"
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSelectProductsMissingSelectedTerms}
                    disabled={assigning}
                  >
                    Selectează lipsă
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    color="warning"
                    onClick={handleApplyTermsToMissingSelectedProducts}
                    disabled={assigning}
                  >
                    Aplică lipsurile
                  </Button>
                </Stack>
              ) : null
            }
          >
            {productsMissingSelectedTerms.length
              ? `${productsMissingSelectedTerms.length} produs${
                  productsMissingSelectedTerms.length === 1 ? "" : "e"
                } nu conțin toți termenii selectați.`
              : "Toate produsele listate conțin termenii selectați."}
          </Alert>
        )}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 4,
          mb: 3,
          background: "rgba(255,255,255,0.9)",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Alocare termeni în masă
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Atribut</InputLabel>
              <Select
                label="Atribut"
                value={selectedAttribute}
                onChange={handleAttributeChange}
              >
                <MenuItem value="">
                  <em>Selectează</em>
                </MenuItem>
                {attributeOptions.map((attr) => (
                  <MenuItem key={attr.value} value={attr.value}>
                    {attr.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <Autocomplete
              multiple
              fullWidth
              disableCloseOnSelect
              options={termOptions}
              filterOptions={filterTermOptions}
              value={selectedTerms}
              onChange={(_, value) => setSelectedTerms(value)}
              isOptionEqualToValue={(option, value) =>
                normalizeText(option.label || option.name || "") ===
                normalizeText(value.label || value.name || "")
              }
              getOptionLabel={(option) => option.label || option.name || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Termeni"
                  placeholder="Alege termeni"
                />
              )}
            />
          </Grid>
        </Grid>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          mt={2}
          justifyContent="flex-end"
        >
          <Button
            variant="text"
            color="warning"
            disabled={assigning || selectedTerms.length === 0}
            onClick={() => setSelectedTerms([])}
          >
            Curăță termenii
          </Button>
          <Button
            variant="outlined"
            startIcon={<ClearAllIcon />}
            disabled={assigning}
            onClick={() => handleAssignTerms(false)}
          >
            Aplică pe selecție
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<DoneAllIcon />}
            disabled={assigning}
            onClick={() => handleAssignTerms(true)}
          >
            Aplică pe toate rezultatele
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{ p: 3, borderRadius: 4, background: "rgba(255,255,255,0.9)" }}
      >
        {productsNeedingSeo.length > 0 && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon fontSize="inherit" />}
            sx={{
              mb: 2,
              borderRadius: 3,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { md: "center" },
              gap: 2,
            }}
            action={
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectMissingSeo}
                  disabled={assigning}
                >
                  Selectează-le
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<AutoFixHighIcon />}
                  onClick={() => handleApplyIncaltaminteSeo()}
                  disabled={assigning}
                >
                  Completează toate
                </Button>
              </Stack>
            }
          >
            <Typography variant="subtitle2" fontWeight={600}>
              {productsNeedingSeo.length} produse de încălțăminte nu au lista
              completă de termeni SEO.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Lista de bază este grupată pentru claritate:
            </Typography>
            <Box mt={1}>
              {incaltaminteClusters.map((cluster, index) => (
                <Box key={`${cluster.title}-${index}`} mb={0.75}>
                  <Typography variant="caption" fontWeight={600}>
                    {cluster.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {" "}
                    – {(cluster.terms || []).join(", ")}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary">
              (Total termeni unici: {requiredIncaltaminteTerms.length})
            </Typography>
          </Alert>
        )}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h6">
            Produse ({displayProducts.length})
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleToggleSelectAll}
            >
              {selectedProducts.size === displayProducts.length
                ? "Debifează tot"
                : "Selectează tot"}
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => setSelectedProducts(new Set())}
            >
              Golește selecția
            </Button>
          </Stack>
        </Stack>
        {!hasSearched ? (
          <Box textAlign="center" py={5}>
            <Typography variant="h6" gutterBottom>
              Aplică filtrele pentru a vedea produsele.
            </Typography>
            <Typography color="text.secondary">
              Pentru performanță, nu încărcăm întregul catalog automat. Alege
              cel puțin un criteriu și apasă „Aplică”.
            </Typography>
          </Box>
        ) : loadingProducts ? (
          <Box textAlign="center" py={5}>
            <CircularProgress />
            <Typography mt={2} color="text.secondary">
              Încărcăm produsele…
            </Typography>
          </Box>
        ) : productsError ? (
          <Alert severity="error">{productsError}</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedProducts.size > 0 &&
                        selectedProducts.size < displayProducts.length
                      }
                      checked={
                        displayProducts.length > 0 &&
                        selectedProducts.size === displayProducts.length
                      }
                      onChange={handleToggleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Produs</TableCell>
                  <TableCell>Categorie</TableCell>
                  <TableCell>Subcategorie</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell align="right">Preț</TableCell>
                  <TableCell align="center">SEO</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayProducts.map((product) => {
                  const isChecked = selectedProducts.has(product.id);
                  const selectionStatus = hasSelectedTermFilter
                    ? selectionStatusByProduct.get(product.id)
                    : null;
                  const missingSelectedLabels =
                    selectionStatus?.missingLabels || [];
                  const hasSelectionMissing =
                    selectionStatus?.missingKeys?.length > 0;
                  return (
                    <TableRow
                      key={product.id}
                      hover
                      sx={
                        product.needsSeoTerms
                          ? { backgroundColor: "rgba(244, 67, 54, 0.08)" }
                          : undefined
                      }
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleSelectProduct(product.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {product.image && (
                            <Box
                              component="img"
                              src={product.image}
                              alt={product.name}
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 2,
                                objectFit: "cover",
                              }}
                            />
                          )}
                          <Box>
                            <Typography fontWeight={600}>
                              {product.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              SKU: {product.sku || "N/A"}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{product.mainCategory || "-"}</TableCell>
                      <TableCell>{product.subCategory || "-"}</TableCell>
                      <TableCell>{product.brand || "-"}</TableCell>
                      <TableCell align="right">
                        {product.price ? `${product.price} Lei` : "-"}
                      </TableCell>
                      <TableCell align="center">
                        <Stack spacing={1} alignItems="center">
                          {product.needsSeoTerms ? (
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="center"
                              alignItems="center"
                            >
                              <Chip
                                label="Termeni lipsă"
                                color="error"
                                size="small"
                              />
                              <Tooltip title="Aplică termenii obligatorii pentru acest produs">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={assigning}
                                    onClick={() =>
                                      handleApplyIncaltaminteSeo([product])
                                    }
                                  >
                                    <AutoFixHighIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          ) : (
                            <Chip
                              icon={<CheckCircleOutlineIcon />}
                              label="Complet"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {hasSelectedTermFilter &&
                            (hasSelectionMissing ? (
                              <Stack spacing={0.5} alignItems="center">
                                <Chip
                                  label="Termeni selectați lipsă"
                                  color="warning"
                                  size="small"
                                />
                                {missingSelectedLabels.length > 0 && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ maxWidth: 220 }}
                                  >
                                    Lipsesc: {missingSelectedLabels.join(", ")}
                                  </Typography>
                                )}
                              </Stack>
                            ) : (
                              <Chip
                                label="Termenii selectați există"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Snackbar
        open={syncSnackbar.open}
        autoHideDuration={4000}
        onClose={closeSyncSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSyncSnackbar}
          severity={syncSnackbar.severity}
          variant="filled"
        >
          {syncSnackbar.message}
        </Alert>
      </Snackbar>
      <Backdrop
        open={assigning}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography>{actionMessage || "Procesăm solicitarea..."}</Typography>
      </Backdrop>
      <Backdrop
        open={syncing}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: "blur(3px)",
        }}
      >
        <Box
          sx={{
            p: 4,
            borderRadius: 3,
            bgcolor: "rgba(15,23,42,0.95)",
            color: "#fff",
            minWidth: 320,
            textAlign: "center",
            boxShadow: "0 24px 60px rgba(15,23,42,0.45)",
          }}
        >
          <Stack spacing={2} alignItems="center">
            <CircularProgress color="inherit" size={36} />
            <Box width="100%">
              <Typography variant="subtitle1" fontWeight={700}>
                Sincronizare produse
              </Typography>
              <Typography variant="body2" color="rgba(226,232,240,0.8)">
                Contactăm serverul local pentru actualizare…
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(syncProgress, 100)}
              sx={{
                width: "100%",
                height: 8,
                borderRadius: 999,
                bgcolor: "rgba(226, 232, 240, 0.2)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 999,
                  bgcolor: "#38bdf8",
                },
              }}
            />
            <Typography variant="subtitle1" fontWeight={600}>
              {Math.round(syncProgress)}%
            </Typography>
          </Stack>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default SEOProductsPage;
